import React, { ForwardedRef, InputHTMLAttributes, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'

import Icon from '@/Icon'
import { useTranslations } from '@/lib/i18nProvider'
import { $Neutral_Black_100 } from '@/scss/useToken'

import { clc } from '../base/utils'

import styles from './Input.module.scss'

interface AuthErrors {
	email: { message?: string }
	password: { message?: string }
	getEmail: { message?: string }
	verifyEmail: { message?: string }
	getPasswords: {
		getPassword: { message?: string }
		confirmPassword: { message?: string }
	}
	registerAgreements: {
		marketing: boolean
		privacy: boolean
		stepin: boolean
		age14: boolean
	}
}

interface SignUpValues {
	getEmail: string
	verifyEmail: string
	getPasswords: {
		getPassword: string
		confirmPassword: string
	}
	registerAgreements: {
		marketing: boolean
		privacy: boolean
		stepin: boolean
		age14: boolean
	}
}

interface LoginValues {
	email: string
	password: string
}

type ErrorKeys = keyof AuthErrors | `${'getPasswords'}.${'getPassword' | 'confirmPassword'}`

interface Props extends InputHTMLAttributes<HTMLInputElement> {
	errors: FieldErrors<AuthErrors>
	keyName: ErrorKeys
	label?: string

	children?: React.ReactNode
	verified?: boolean
	setValue?: (name: any, value: any) => void
	// setValue?: UseFormSetValue<SignInType | SignUpType>
	// setValue?: (name: string | Object, value: unknown, config?: Object) => void
	getValues?: (payload?: string | string[]) => SignUpValues | LoginValues | unknown
}

const AuthInput = ({ errors, keyName, label, children, verified, setValue, getValues, ...props }: Props) => {
	const [capsLock, setCapsLock] = useState(false)
	const [showError, setShowError] = useState(false)

	const e = useTranslations('errorText')
	const s = useTranslations('signupText')

	const t = keyName === 'email' || keyName === 'password' ? e : s

	const checkCapsLock = (e: React.KeyboardEvent) => {
		setCapsLock(e.getModifierState('CapsLock'))
	}

	useEffect(() => {
		setShowError(!capsLock)
	}, [capsLock])

	const capsLockEl = (type: string | undefined) => {
		if (type === 'password') {
			if (keyName.includes('getPasswords')) {
				return (
					<>
						<input onKeyUp={checkCapsLock} {...props} />
						{capsLock && (
							<div className={styles.errorArea}>
								<span className={styles.error}>{t(`${keyName}.capsLockError` as keyof typeof t)}</span>
							</div>
						)}
					</>
				)
			}
			return (
				<>
					<input onKeyUp={checkCapsLock} {...props} />
					{capsLock && (
						<div className={styles.errorArea}>
							<span className={styles.error}>{t('password.capsLockError' as keyof typeof t)}</span>
						</div>
					)}
				</>
			)
		}

		if (children) {
			return (
				<div className={styles.row}>
					<div style={{ position: 'absolute' }}>{children}</div>
					<input onKeyUp={checkCapsLock} {...props} />
				</div>
			)
		}
		return <input onKeyUp={checkCapsLock} {...props} />
	}

	const getErrorMessage = () => {
		if (keyName.includes('getPasswords')) {
			const [keyName1, keyName2] = keyName.split('.') as ['getPasswords', 'getPassword' | 'confirmPassword']

			const error = errors[keyName1]?.[keyName2]?.message
		} else {
			const error = errors[keyName as keyof typeof errors]?.message
			return error ? t(`${keyName}.${error}` as keyof typeof t) : ''
		}
	}

	const errorMessage = getErrorMessage()

	const iconBasedOnType = (type: string | undefined) => {
		const iconMap: Record<
			string,
			| 'Action_PasswordCheck_24_Default'
			| 'Action_Email_24_Default'
			| 'Action_Password_24_Default'
			| 'Action_Code_24_Default'
		> = {
			password:
				keyName === 'getPasswords.confirmPassword' ? 'Action_PasswordCheck_24_Default' : 'Action_Password_24_Default',
			email: 'Action_Email_24_Default',
			number: 'Action_Code_24_Default',
		}

		const iconPath = iconMap[type as keyof typeof iconMap]

		return iconPath ? (
			<div className={styles.leftIconArea}>
				<Icon path={iconPath} alt={iconPath} className={styles.icon} />
			</div>
		) : null
	}

	const handleInputErase = () => setValue && setValue(keyName, '')

	const checkOrEraseBasedOnType = (type: string | undefined) => {
		const hasValue = getValues && !!getValues(keyName)

		if (!hasValue) return null

		if (type === 'password' || type === 'email') {
			return (
				<div className={styles.rightIconArea} onClick={handleInputErase}>
					<Icon path="Action_Clear_24_Purple" alt="inputErase" className={styles.icon} />
				</div>
			)
		}
		if (type === 'number') {
			return (
				<div className={styles.rightIconArea}>
					<Icon
						path="Action_Clear_24_Purple"
						alt="inputCheck"
						className={verified ? clc(styles.icon, styles.verified) : styles.icon}
					/>
				</div>
			)
		}
		return null
	}

	return (
		<label className={styles.label}>
			{iconBasedOnType(props.type)}
			{capsLockEl(props.type)}
			{checkOrEraseBasedOnType(props.type)}

			{!capsLock && showError && errorMessage && (
				<div className={styles.errorArea}>
					{/* @ts-ignore */}
					<span className={styles.error}>{errorMessage}</span>
				</div>
			)}
		</label>
	)
}

export default AuthInput
